import React from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { createLinkToken, exchangePublicToken, getTransactions, getAccounts } from '../services/plaid';
import { Loader2, AlertTriangle } from 'lucide-react';

interface PlaidLinkProps {
  onSuccess: (data: {
    transactions: any[];
    accounts: any[];
  }) => void;
  onExit?: () => void;
}

const PlaidLink: React.FC<PlaidLinkProps> = ({ onSuccess, onExit }) => {
  const [linkToken, setLinkToken] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const init = async () => {
      try {
        const token = await createLinkToken();
        setLinkToken(token);
        setError(null);
      } catch (error) {
        console.error('Error initializing Plaid:', error);
        setError('Unable to initialize bank connection. Please try again later.');
      }
    };
    init();
  }, []);

  const onPlaidSuccess = React.useCallback(async (publicToken: string) => {
    try {
      if (!navigator.onLine) {
        setError('Cannot connect to bank while offline');
        return;
      }
      
      setIsLoading(true);
      const accessToken = await exchangePublicToken(publicToken);
      const [transactions, accounts] = await Promise.all([
        getTransactions(accessToken),
        getAccounts(accessToken)
      ]);
      
      onSuccess({ transactions, accounts });
    } catch (error) {
      console.error('Error in Plaid success callback:', error);
      setError('Failed to connect to your bank. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (public_token) => onPlaidSuccess(public_token),
    onExit: () => {
      setError(null);
      onExit?.();
    },
  });

  if (isLoading) {
    return (
      <button
        disabled
        className="w-full flex items-center justify-center gap-2 px-4 py-3 
                   bg-blue-600 text-white font-medium rounded-lg shadow-sm opacity-70"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        Connecting to Bank...
      </button>
    );
  }

  return (
    <div className="space-y-3">
      <button
        onClick={() => open()}
        disabled={!ready || !navigator.onLine}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 
                   bg-blue-600 hover:bg-blue-700 active:bg-blue-800 
                   text-white font-medium rounded-lg shadow-sm 
                   transition-colors disabled:opacity-50"
      >
        {!navigator.onLine ? 'Offline - Cannot Connect Bank' : 'Connect Your Bank Account'}
      </button>
      
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
      
      <p className="text-xs text-gray-500 text-center">
        Securely connect your bank account to get personalized financial insights
      </p>
    </div>
  );
}

export default PlaidLink;